'use client';
import { useEffect, useState, useMemo } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { arrayRemove } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import Modal from 'react-modal';
import getUserByAuthId from '../components/getUserDataByAuthId';

// helper to chunk into <=10 items for "in" queries
function chunk(arr, size = 10) {
  return Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
    arr.slice(i * size, i * size + size)
  );
}

export function BlockedUsers() {
  const [user] = useAuthState(auth);
  const [myDocId, setMyDocId] = useState('');
  const [blockedUids, setBlockedUids] = useState([]);
  const [blockedProfiles, setBlockedProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unblocking, setUnblocking] = useState(null);
  const [reportingUid, setReportingUid] = useState(null);
  const [reportReason, setReportReason] = useState('spam');
  const [reportNotes, setReportNotes] = useState('');
  const [refresh, setRefresh] = useState(0);

  // load my user doc and blocked list
  useEffect(() => {
    if (!user) return;
    (async () => {
      setLoading(true);
      try {
        const meQ = query(collection(db, 'users'), where('authUID', '==', user.uid));
        const meSnap = await getDocs(meQ);
        if (!meSnap.empty) {
          const meDoc = meSnap.docs[0];
          setMyDocId(meDoc.id);
          const data = meDoc.data() || {};
          const list = Array.isArray(data.blocked) ? data.blocked : [];
          setBlockedUids(list);
        } else {
          setMyDocId('');
          setBlockedUids([]);
        }
      } catch (e) {
        console.error('load self error', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [user, refresh]);

  // fetch profiles of blocked users
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const chunks = chunk(blockedUids, 10);
        const all = [];
        for (const c of chunks) {
          if (c.length === 0) continue;
          const qUsers = query(collection(db, 'users'), where('authUID', 'in', c));
          const snap = await getDocs(qUsers);
          snap.forEach(d => all.push({ id: d.id, ...d.data() }));
        }
        all.sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));
        setBlockedProfiles(all);
      } catch (e) {
        console.error('load blocked profiles error', e);
      }
    })();
  }, [blockedUids, user]);

  const nothingBlocked = useMemo(
    () => !loading && blockedUids.length === 0,
    [loading, blockedUids.length]
  );

  const handleUnblock = async (blockedAuthUid) => {
    if (!myDocId) return;
    setUnblocking(blockedAuthUid);
    try {
      await updateDoc(doc(db, 'users', myDocId), {
        blocked: arrayRemove(blockedAuthUid),
      });
      setRefresh(t => t + 1);
    } catch (e) {
      console.error('unblock failed', e);
      alert('Sorry, something went wrong unblocking that user.');
    } finally {
      setUnblocking(null);
    }
  };

  const submitReport = async () => {
    if (!reportingUid || !user) return;
    try {
      
      const reporterData = await getUserByAuthId(user.uid)
      const reportedData = await getUserByAuthId(reportingUid)
      
      await addDoc(collection(db, 'reports'), {
        reporterName: reporterData?.displayName ?? '-',
        reporterUID: user.uid,
        reportedUID: reportingUid,
        reportedName: reportedData?.displayName ?? '-',
        reason: reportReason,
        notes: reportNotes || '',
        createdAt: serverTimestamp(),
      });
      setReportingUid(null);
      setReportReason('spam');
      setReportNotes('');
      alert('Thanks for your report. Our team will review it.');
    } catch (e) {
      console.error('report failed', e);
      alert('Unable to file the report right now.');
    }
  };

  if (!user) {
    return (
      <div className="main settings blocked">
        <div className=""><p>Please sign in to view blocked users.</p></div>
      </div>
    );
  }

  return (
    <div className="main settings blocked">
      <Modal
        isOpen={!!reportingUid}
        onRequestClose={() => setReportingUid(null)}
        className="report-modal"
        contentLabel="Report user"
      >
        <h2>Report user</h2>
        <label>
          Reason
          <select value={reportReason} onChange={e => setReportReason(e.target.value)}>
            <option value="spam">Spam</option>
            <option value="harassment">Harassment</option>
            <option value="hate">Hate speech</option>
            <option value="nudity">Illegal content</option>
            <option value="other">Other</option>
          </select>
        </label>
        <label>
          Details (optional)
          <textarea
            placeholder="Add any further information"
            value={reportNotes}
            onChange={e => setReportNotes(e.target.value)}
          />
        </label>
        <div className="actions">
          <button onClick={() => setReportingUid(null)}>Cancel</button>
          <button onClick={submitReport}>Submit report</button>
        </div>
      </Modal>

      <div className="">
        <h1>Blocked users</h1>

        {loading && <p>Loading…</p>}
        {nothingBlocked && <p>You haven’t blocked anyone yet.</p>}

        {!loading && blockedProfiles.length > 0 && (
          <ul className="blocked-list">
            {blockedProfiles.map(u => (
              <li key={u.authUID} className="blocked-card">
                <img
                  className="avatar"
                  src={u.displayPhoto || '/placeholder-avatar.png'}
                  alt={`${u.displayName || 'User'} avatar`}
                />
                <div className="info">
                  <div className="name">@{u.displayName || '(no name)'}</div>
                </div>
                <div className="actions">
                  <button
                    disabled={unblocking === u.authUID}
                    onClick={() => handleUnblock(u.authUID)}
                  >
                    {unblocking === u.authUID ? 'Unblocking…' : 'Unblock'}
                  </button>
                  <button onClick={() => setReportingUid(u.authUID)}>Report</button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <style>{`
        .blocked-list { list-style: none; padding: 0; margin: 1rem 0; display: grid; gap: 12px; }
        .blocked-card {
          display: grid;
          grid-template-columns: 56px 1fr auto;
          gap: 12px;
          align-items: center;
          padding: 10px 12px;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          background: #fff;
        }
        .avatar { width: 56px; height: 56px; border-radius: 12px; object-fit: cover; }
        .name { font-weight: 600; }
        .actions { display: flex; gap: 8px; }
        .actions button { padding: 8px 10px; border-radius: 8px; border: 1px solid #e5e7eb; background: #f9fafb; cursor: pointer; }
        .actions button:hover { background: #f3f4f6; }
        .report-modal { background: #fff; padding: 20px; border-radius: 12px; max-width: 480px; margin: 10vh auto; outline: none; }
        .report-modal h2 { margin-top: 0; }
        .report-modal label { display: block; margin: 10px 0; }
        .report-modal .actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 12px; }
      `}</style>
    </div>
  );
}